import React, { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import bannerPurpleMetricsWeb from 'assets/images/banner-purple-metrics-web.png';
import bannerPurpleMetricsMobile from 'assets/images/banner-purple-metrics-mobile.png';
import bannerPesquisaUsabilidadeWeb from 'assets/images/banner-pesquisa-usabilidade-indecx.jpg';
import bannerPesquisaUsabilidadeMobile from 'assets/images/banner-pesquisa-usabilidade-indecx-mobile.jpg';

import { getConsultants } from 'services/consultantsServices';
import { getUserData } from 'services/indexcServices';
import { buildUsabilitySurveyUrl } from 'utils/buildUsabilitySurveyUrl';

import { HOME_BANNER_CAROUSEL_INTERVAL_MS } from '../homeBannerConfig';
import { ImageBannerPurpleMetrics, LinkPurpleMetrics } from '../styles';

type HomeBannerCarouselProps = {
  brandSurveyUrl: string;
};

const slideLinkStyle = (visible: boolean): React.CSSProperties => ({
  position: 'absolute',
  left: 0,
  top: 0,
  width: '100%',
  opacity: visible ? 1 : 0,
  pointerEvents: visible ? 'auto' : 'none',
  transition: 'opacity 0.45s ease',
  zIndex: visible ? 2 : 1,
  cursor: visible ? 'pointer' : 'default',
});

const HomeBannerCarousel = ({
  brandSurveyUrl,
}: HomeBannerCarouselProps): JSX.Element => {
  const isMobile = useMediaQuery({ maxWidth: 620 });
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [usabilityUrl, setUsabilityUrl] = useState(() =>
    buildUsabilitySurveyUrl({})
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduceMotion(mq.matches);
    const handler = (): void => setReduceMotion(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [userData, consultants] = await Promise.all([
        getUserData(),
        getConsultants(),
      ]);
      if (cancelled) return;
      const consultantName = consultants?.[0]?.name || '';
      setUsabilityUrl(
        buildUsabilitySurveyUrl({
          userData: userData ?? null,
          consultantName,
        })
      );
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (reduceMotion || paused) return undefined;
    const id = window.setInterval(() => {
      setActiveIndex((i) => (i + 1) % 2);
    }, HOME_BANNER_CAROUSEL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [reduceMotion, paused]);

  const viewportMinHeight = isMobile ? 62 : 98;

  return (
    <div
      role="region"
      aria-roledescription="carousel"
      aria-label="Banners informativos"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{ width: '100%' }}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          minHeight: viewportMinHeight,
        }}
      >
        <LinkPurpleMetrics
          href={brandSurveyUrl}
          rel="noreferrer"
          target="_blank"
          style={slideLinkStyle(activeIndex === 0)}
        >
          <ImageBannerPurpleMetrics
            src={isMobile ? bannerPurpleMetricsMobile : bannerPurpleMetricsWeb}
            alt="Pesquisa de marca — abre em nova aba"
          />
        </LinkPurpleMetrics>
        <LinkPurpleMetrics
          href={usabilityUrl}
          rel="noreferrer"
          target="_blank"
          style={slideLinkStyle(activeIndex === 1)}
        >
          <ImageBannerPurpleMetrics
            src={
              isMobile
                ? bannerPesquisaUsabilidadeMobile
                : bannerPesquisaUsabilidadeWeb
            }
            alt="Pesquisa de usabilidade — abre em nova aba"
          />
        </LinkPurpleMetrics>
      </div>
      <div
        role="tablist"
        aria-label="Selecionar banner"
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 6,
          marginTop: 6,
        }}
      >
        {[0, 1].map((i) => (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={activeIndex === i}
            aria-label={
              i === 0
                ? 'Exibir banner pesquisa de marca'
                : 'Exibir banner pesquisa de usabilidade'
            }
            onClick={() => setActiveIndex(i)}
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              background: activeIndex === i ? '#0B29FB' : '#ccc',
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default HomeBannerCarousel;
